import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import {
  addDays,
  formatDate,
  isSameDay,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Navigate, useTypedParams } from '@sorare/routing';
import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import {
  Dropdown,
  DropdownOptionLabel,
} from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { SwitchIcon } from '@sorare/core/src/atoms/icons/SwitchIcon';
import { CheckboxGroup } from '@sorare/core/src/atoms/inputs/CheckboxGroup';
import { FilterAccordion } from '@sorare/core/src/atoms/layout/Accordion';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandL,
  BrandXS,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { FiltersDrawer } from '@sorare/core/src/components/FiltersDrawer';
import { ListWithDetailLayout } from '@sorare/core/src/components/layout/ListWithDetailLayout';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import {
  ANY_SPORT_SCORES_MATCHES_DAY,
  ANY_SPORT_SCORES_MATCHES_DAY_GAMEID,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { sortBy } from '@sorare/core/src/lib/arrays';
import {
  playableBlockchainRarities,
  scarcityNames,
} from '@sorare/core/src/lib/cards';
import {
  fantasy,
  navLabels,
  playerDetails,
} from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { FiltersGroup } from '@sorare/marketplace/src/search/FiltersGroup';
import {
  isGameLeft,
  isGameLive,
} from '@sorare/marketplace/src/shared/game/lib';

import stadiumBackground from '#src/assets/play-now-desktop.webp';
import { LiveGamesFilter } from '#src/components/scoutingCenter/LiveGamesFilter/index.tsx';
import { TodayButton } from '#src/components/scoutingCenter/TodayButton/index.tsx';
import { DatePicker } from '#src/components/scoutingCenter/gameCenter/DatePicker/index.tsx';
import { CompetitionsFilter } from '#src/components/scoutingCenter/gameCenter/filters/CompetitionsFilter/index.tsx';
import { getCompetitionPictureUrl } from '#src/lib/so5.ts';

import { EmptyState } from './EmptyState/index.tsx';
import { GamesSection } from './GamesSection/index.tsx';
import type {
  GameCenterDayLayoutQuery,
  GameCenterDayLayoutQueryVariables,
} from './__generated__/index.graphql.ts';

const Background = styled(Vertical)`
  position: relative;
  isolation: isolate;

  --list-panel-padding: var(--double-unit);

  @media ${laptopAndAbove} {
    --list-panel-padding: var(--triple-unit);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    width: 100%;
    background:
      linear-gradient(0deg, var(--c-black) 0%, var(--c-nl-50) 100%),
      url(${stadiumBackground}) no-repeat top center;
    background-size: cover;
    z-index: -1;
  }
`;

const BodyWrapper = styled(Vertical)`
  gap: var(--unit);
  @media ${laptopAndAbove} {
    gap: var(--double-unit);
  }
`;

const FiltersWrapper = styled(Horizontal)`
  padding: 0 var(--list-panel-padding);
`;

const ListWrapper = styled(Vertical).attrs({ gap: 1.5 })`
  padding: 0 var(--list-panel-padding) var(--double-unit);
`;

const HeaderWrapper = styled(Vertical)`
  padding: var(--list-panel-padding);
  position: relative;
  isolation: isolate;
`;

const LiveGames = styled(Horizontal)`
  &.hidden {
    visibility: hidden;
  }
`;

const GAME_CENTER_DAY_LAYOUT_QUERY = gql`
  query GameCenterDayLayoutQuery(
    $fromDate: ISO8601DateTime!
    $toDate: ISO8601DateTime!
    $sport: Sport!
    $rarities: [Rarity!]
    $withCards: Boolean!
  ) {
    competitionGames(fromDate: $fromDate, toDate: $toDate, sport: $sport) {
      gamesWithMyCardPreview(withCards: $withCards, rarities: $rarities) {
        id
        game {
          id
          statusTyped
          date
        }
        ...GameSection_gameWithCardsPreview
      }
      competition {
        slug
        ...getCompetitionPictureUrl_competition
        ...CompetitionsFilter_competition
      }
      games {
        id
      }
    }
  }
  ${CompetitionsFilter.fragments.competition}
  ${GamesSection.fragments.gameWithCardsPreview}
  ${getCompetitionPictureUrl.fragments.competition}
` as TypedDocumentNode<
  GameCenterDayLayoutQuery,
  GameCenterDayLayoutQueryVariables
>;

type SortOption = 'date' | 'competition';

const sortOptions = defineMessages({
  date: {
    id: 'GameCenterDayLayout.sort.date',
    defaultMessage: 'Date',
  },
  competition: {
    id: 'GameCenterDayLayout.sort.competition',
    defaultMessage: 'Competition',
  },
});

const CompetitionLogo = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

type Filters = {
  rarities: Rarity[];
  competitions: string[];
  sort: SortOption;
  withCards: boolean;
};
export const GameCenterDayLayout = <
  Params extends {
    day: string;
    gameId?: string;
  },
>() => {
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const { day, gameId } = useTypedParams<Params>();
  const { sport, generateSportContextPath } = useSportContext();
  const { rarities: userRarities } = useCurrentUserContext();
  const [dates, setDates] = useState<Record<string, Date>>({});
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [liveAndUpcomingGamesFilter, setLiveAndUpcomingGamesFilter] =
    useState(true);

  const [filters, setFilters] = useState<Filters>({
    rarities: userRarities.length > 0 ? userRarities : [Rarity.common],
    competitions: [],
    sort: lifecycle?.matchCenterFilters?.sort || 'date',
    withCards:
      lifecycle?.matchCenterFilters?.withCards === undefined
        ? true
        : lifecycle.matchCenterFilters.withCards,
  });

  const updateFilter = (filter: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...filter }));
  };

  const { sort, withCards, competitions, rarities } = filters;
  const fromDate = startOfDay(parseISO(day));

  const { data, previousData, loading } = useQuery(
    GAME_CENTER_DAY_LAYOUT_QUERY,
    {
      variables: {
        sport,
        fromDate,
        toDate: addDays(fromDate, 1),
        rarities,
        withCards,
      },
    }
  );

  const isDesktopAndAbove = useIsDesktopAndAbove();

  const navigate = useNavigate();

  const competitionGames = (data || previousData)?.competitionGames || [];

  const todayLiveGamesCount = competitionGames
    .flatMap(competition => competition.gamesWithMyCardPreview)
    .filter(game => isGameLive(game.game.statusTyped)).length;

  const gameIdToCompetitionSlug = competitionGames.reduce<
    Record<string, string>
  >((acc, c) => {
    c.games.forEach(g => {
      acc[g.id] = c.competition.slug;
    });
    return acc;
  }, {});

  const isGameFiltered = (
    gameWithMyCardPreview: GameCenterDayLayoutQuery['competitionGames'][number]['gamesWithMyCardPreview'][number]
  ) => {
    if (
      competitions.length > 0 &&
      !competitions.includes(
        gameIdToCompetitionSlug[gameWithMyCardPreview.game.id]
      )
    ) {
      return false;
    }

    if (
      liveAndUpcomingGamesFilter &&
      !isGameLeft(gameWithMyCardPreview.game.statusTyped)
    ) {
      return false;
    }

    return true;
  };

  const allGames = sortBy(
    game => game.game.date,
    [...competitionGames.flatMap(game => game.gamesWithMyCardPreview)]
  ).filter(game => isGameFiltered(game));
  const allGamesLeft = allGames.filter(game =>
    isGameLeft(game.game.statusTyped)
  );

  const dayAsDate = startOfDay(parseISO(day));
  const today = startOfDay(new Date());
  const isToday = isSameDay(dayAsDate, today);

  // Show today and +-10 days
  // Need to use WithSlug format to use DateOrFixturePicker
  const tenDaysAgo = subDays(dayAsDate, 10);
  Array.from({ length: 21 }, (_, i) => {
    const date = addDays(tenDaysAgo, i);
    const dateAsString = date.toISOString();
    if (!dates[dateAsString]) {
      setDates(prev => ({ ...prev, [dateAsString]: date }));
    }
    return undefined;
  });

  const datesForSelector = Object.keys(dates)
    .sort((a, b) => (b < a ? -1 : 1))
    .map(k => ({
      date: dates[k],
      slug: k,
    }));

  const firstGame = allGames.at(0);

  if (liveAndUpcomingGamesFilter && allGamesLeft.length === 0 && !loading) {
    setLiveAndUpcomingGamesFilter(false);
  }

  // on mobile we should display the list, on desktop we navigate to the first game
  let redirection = null;
  if (!gameId && firstGame?.game.id && isDesktopAndAbove && !loading) {
    // do not render just <Navigate> to prevent a flash of content
    redirection = (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_SCORES_MATCHES_DAY_GAMEID, {
          params: {
            day,
            gameId: idFromObject(firstGame.game.id),
          },
        })}
        replace
      />
    );
  }

  const displayedCompetitionGames = competitionGames
    .map(cg => ({
      ...cg,
      displayedGames: cg.gamesWithMyCardPreview.filter(gameWithMyCardPreview =>
        isGameFiltered(gameWithMyCardPreview)
      ),
    }))
    .filter(
      cg =>
        cg.displayedGames.length > 0 &&
        (competitions.length === 0 ||
          (competitions.length > 0 &&
            competitions.includes(cg.competition.slug)))
    );

  const hasMultipleCompetitions = competitionGames.length > 0;

  const raritiesOptions = [Rarity.common, ...playableBlockchainRarities].map(
    rarity => ({
      value: rarity,
      label: (
        <Horizontal>
          <ScarcityIcon rarity={rarity} /> {scarcityNames[rarity]}
        </Horizontal>
      ),
    })
  );

  const useCompetitionGrouping = sport === Sport.FOOTBALL;
  const noFilters: Filters = {
    competitions: [],
    rarities: [],
    sort: 'date',
    withCards: false,
  };
  const someFiltersSet = Object.entries(filters).some(
    ([key, value]) => noFilters[key as keyof Filters] !== value
  );

  return (
    <>
      <ListWithDetailLayout noPadding noRightPanelPadding>
        <Background gap={0}>
          <HeaderWrapper gap={2.5}>
            <SBHorizontal>
              <LiveGames
                gap={0.5}
                className={classNames({ hidden: !todayLiveGamesCount })}
              >
                <LiveDot animate color="var(--c-red-400)" />
                <LabelM color="var(--c-red-400)">
                  <FormattedMessage
                    {...playerDetails.gamesCount}
                    values={{ count: todayLiveGamesCount }}
                  />
                </LabelM>
              </LiveGames>
              <IconButton
                color="quaternary"
                onClick={() => setShowFiltersDrawer(true)}
                icon={faFilter}
                size="small"
              />
              <FiltersDrawer
                open={showFiltersDrawer}
                onClose={() => setShowFiltersDrawer(false)}
              >
                <FiltersGroup>
                  <div className="FilterWidget visible">
                    <FilterAccordion
                      startsOpen
                      title={
                        <LabelL bold>
                          <FormattedMessage {...fantasy.scarcities} />
                        </LabelL>
                      }
                    >
                      <CheckboxGroup
                        selectedValues={rarities || []}
                        options={raritiesOptions}
                        onChange={v => updateFilter({ rarities: v })}
                      />
                    </FilterAccordion>
                    {competitionGames.length > 1 &&
                      !!useCompetitionGrouping && (
                        <CompetitionsFilter
                          competitions={competitionGames.map(
                            ({ competition, games }) => ({
                              competition,
                              count: games.length,
                            })
                          )}
                          selected={competitions}
                          onChange={competitionSlugs =>
                            updateFilter({ competitions: competitionSlugs })
                          }
                        />
                      )}
                  </div>
                </FiltersGroup>
              </FiltersDrawer>
            </SBHorizontal>
            <BrandL>
              <FormattedMessage
                {...(sport === Sport.FOOTBALL
                  ? fantasy.matchCenter
                  : fantasy.gameCenter)}
              />
            </BrandL>
            <DatePicker
              dates={datesForSelector}
              value={{ slug: dayAsDate.toISOString(), date: dayAsDate }}
              onChange={newDate => {
                updateFilter({ competitions: [] });
                navigate(
                  generateSportContextPath(ANY_SPORT_SCORES_MATCHES_DAY, {
                    params: {
                      day: formatDate(startOfDay(newDate.date), 'yyyy-MM-dd'),
                    },
                  })
                );
              }}
            />
          </HeaderWrapper>
          <BodyWrapper>
            <FullViewportScroll>
              <FiltersWrapper>
                {isToday ? (
                  <LiveGamesFilter
                    checked={liveAndUpcomingGamesFilter}
                    disabled={allGamesLeft.length === 0}
                    onChange={() =>
                      setLiveAndUpcomingGamesFilter(prev => !prev)
                    }
                  />
                ) : (
                  <TodayButton
                    withLiveDot={!!todayLiveGamesCount}
                    onClick={() => {
                      setLiveAndUpcomingGamesFilter(false);
                      navigate(
                        generateSportContextPath(ANY_SPORT_SCORES_MATCHES_DAY, {
                          params: {
                            day: formatDate(today, 'yyyy-MM-dd'),
                          },
                        })
                      );
                    }}
                  />
                )}
                {/* TODO: Restore it when adding sorting */}
                <Button
                  color="tertiary"
                  size="small"
                  onClick={() => {
                    updateFilter({ withCards: !filters.withCards });
                    updateLifecycle(LIFECYCLE.matchCenterFilters, {
                      ...lifecycle?.matchCenterFilters,
                      ...{
                        withCards: !filters.withCards,
                      },
                    });
                  }}
                  disableDebounce
                >
                  <FormattedMessage {...navLabels.myCards} />
                  <SwitchIcon size="small" checked={withCards} />
                </Button>
                {!!useCompetitionGrouping && (
                  <Dropdown
                    label={props => (
                      <Button
                        {...props}
                        size="small"
                        color="tertiary"
                        endIcon={<ChevronDownBold />}
                      >
                        <FormattedMessage {...sortOptions[sort]} />
                      </Button>
                    )}
                  >
                    {({ closeDropdown }) => (
                      <>
                        {(['date', 'competition'] as SortOption[]).map(
                          sortOption => (
                            <DropdownOptionLabel
                              className={classNames({
                                selected: sort === sortOption,
                              })}
                              key={sortOption}
                              onClick={() => {
                                updateFilter({ sort: sortOption });
                                updateLifecycle(LIFECYCLE.matchCenterFilters, {
                                  ...lifecycle?.matchCenterFilters,
                                  ...{
                                    sort: sortOption,
                                  },
                                });
                                closeDropdown();
                              }}
                            >
                              <FormattedMessage {...sortOptions[sortOption]} />
                            </DropdownOptionLabel>
                          )
                        )}
                      </>
                    )}
                  </Dropdown>
                )}
              </FiltersWrapper>
            </FullViewportScroll>
            {allGames.length > 0 || loading ? (
              <ListWrapper>
                {sort === 'date' ? (
                  <GamesSection
                    selectedGameId={gameId}
                    games={allGames}
                    cardsRarities={rarities}
                    loading={loading}
                  />
                ) : (
                  displayedCompetitionGames.map(
                    ({ competition, displayedGames }) => (
                      <GamesSection
                        key={competition.slug}
                        selectedGameId={gameId}
                        title={
                          <Horizontal>
                            <CompetitionLogo
                              src={getCompetitionPictureUrl(competition)}
                              alt={competition.name}
                              width={20}
                              height={20}
                            />
                            <BrandXS>
                              {hasMultipleCompetitions
                                ? competition.name
                                : undefined}
                            </BrandXS>
                          </Horizontal>
                        }
                        games={displayedGames}
                        cardsRarities={rarities}
                        loading={loading}
                      />
                    )
                  )
                )}
              </ListWrapper>
            ) : (
              <>
                {someFiltersSet ? (
                  <EmptyState
                    subtitle={
                      <FormattedMessage
                        id="GameCenterEmptyState.filters.subtitle"
                        defaultMessage="Try to change the filters to view more games"
                      />
                    }
                    cta={
                      <Button
                        size="medium"
                        color="secondary"
                        onClick={() => updateFilter(noFilters)}
                      >
                        <FormattedMessage
                          id="GameCenterEmptyState.filters.cta"
                          defaultMessage="Clear Filters"
                        />
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    subtitle={
                      <FormattedMessage
                        id="GameCenterEmptyState.noGamesToday.subtitle"
                        defaultMessage="There are no upcoming matches today"
                      />
                    }
                    cta={
                      <Button
                        size="medium"
                        color="primary"
                        to={generateSportContextPath(
                          ANY_SPORT_SCORES_MATCHES_DAY,
                          {
                            params: {
                              day: formatDate(
                                addDays(fromDate, 1),
                                'yyyy-MM-dd'
                              ),
                            },
                          }
                        )}
                      >
                        <FormattedMessage
                          id="GameCenterEmptyState.noGamesToday.cta"
                          defaultMessage="Jump To Tomorrow"
                        />
                      </Button>
                    }
                  />
                )}
              </>
            )}
          </BodyWrapper>
        </Background>
      </ListWithDetailLayout>
      {redirection}
    </>
  );
};
